import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import { allTravelDnaSteps } from '@/_constants/TravelDnaStep.ts';
import TravelDnaStep from '@/_constants/TravelDnaStep.ts';
import { dnaSchema } from '$/requests/UpsertDnaRequest.ts';
import SplurgePriority, {
  allSplurgePriorities,
} from '$/_const/dna/SplurgePriority.ts';
import type { SplurgePriority as SplurgePriorityType } from '$/_const/dna/SplurgePriority.ts';
import type { TravelDnaFormData } from '$/models/Dna.d.ts';
import type { OnCompleteArgs } from '@/components/functional/TravelDnaManipulationForm/TravelDnaManipulationForm.tsx';
import DnaStepLayout from '@/components/functional/TravelDnaManipulationForm/shared/DnaStepLayout.tsx';
import DnaChoiceTile from '@/components/functional/TravelDnaManipulationForm/shared/DnaChoiceTile.tsx';
import DnaPrimaryButton from '@/components/functional/TravelDnaManipulationForm/shared/DnaPrimaryButton.tsx';
import DnaError from '@/components/functional/TravelDnaManipulationForm/shared/DnaError.tsx';
import DnaIcon from '@/components/functional/TravelDnaManipulationForm/shared/DnaIcon.tsx';

const yourSplurgesSchema = dnaSchema.pick({
  what_do_you_splurge_on: true,
});

export type YourSplurgesSchema = z.infer<typeof yourSplurgesSchema>;

type YourSplurgesProps = {
  onComplete?: (args: OnCompleteArgs) => Promise<void>;
  onCompleteWhenDirty?: (args: OnCompleteArgs) => Promise<void>;
};

const visibleOptions = allSplurgePriorities.filter(
  (option) => option !== SplurgePriority.NONE
);

const splurgeIcons: Record<SplurgePriorityType, ReactNode> = {
  food_and_drink: <DnaIcon name="food" />,
  accommodation: <DnaIcon name="accommodation" />,
  flights_and_transport: <DnaIcon name="transport" />,
  experiences_and_activities: <DnaIcon name="events" />,
  culture_and_attractions: <DnaIcon name="culture" />,
  wellness: <DnaIcon name="wellness" />,
  shopping: <DnaIcon name="shopping" />,
  nightlife: <DnaIcon name="nightlife" />,
  none: <span />,
};

function YourSplurges({
  onComplete,
  onCompleteWhenDirty,
}: YourSplurgesProps) {
  const { t } = useTranslation();
  const { nextStep, setContextData, contextData } = useStepperContext<
    typeof allTravelDnaSteps,
    TravelDnaFormData
  >();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<YourSplurgesSchema>({
    resolver: zodResolver(yourSplurgesSchema),
    defaultValues: {
      what_do_you_splurge_on: contextData?.what_do_you_splurge_on ?? [],
    },
  });

  const onSubmit = (data: YourSplurgesSchema) => {
    void (async () => {
      try {
        setIsPending(true);
        setError(null);
        const context = { ...contextData, ...data };
        const args: OnCompleteArgs = {
          step: TravelDnaStep.YOUR_SPLURGES,
          data,
          context,
        };

        if (isDirty) {
          setContextData((previous) => ({ ...previous, ...data }));
          await onCompleteWhenDirty?.(args);
        }

        await onComplete?.(args);
        nextStep();
      } catch (caughtError) {
        setError(
          caughtError instanceof Error
            ? caughtError
            : new Error(String(caughtError))
        );
      } finally {
        setIsPending(false);
      }
    })();
  };

  return (
    <DnaStepLayout
      eyebrow={t('travelDnaPage.yourSplurges.header')}
      title={t('travelDnaPage.yourSplurges.subheader')}
      description={t('travelDnaPage.yourSplurges.description')}
      maxWidthClassName="max-w-[790px]"
    >
      <form
        className="mt-6 flex w-full flex-col items-center"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <DnaError error={error} className="mb-6" />

        <Controller
          control={control}
          name="what_do_you_splurge_on"
          render={({ field, fieldState }) => {
            const selected = field.value ?? [];
            const hasNone = selected.includes(SplurgePriority.NONE);
            const atLimit = selected.length >= 3;

            const toggle = (option: SplurgePriorityType) => {
              if (option === SplurgePriority.NONE) {
                field.onChange(hasNone ? [] : [SplurgePriority.NONE]);
                return;
              }

              if (selected.includes(option)) {
                field.onChange(selected.filter((item) => item !== option));
              } else if (!atLimit) {
                field.onChange([
                  ...selected.filter((item) => item !== SplurgePriority.NONE),
                  option,
                ]);
              }
            };

            return (
              <>
                <div className="grid w-full grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-5">
                  {visibleOptions.map((option) => {
                    const isSelected = selected.includes(option);
                    return (
                      <DnaChoiceTile
                        key={option}
                        label={t(
                          `travelDnaPage.yourSplurges.options.${option}`
                        )}
                        icon={splurgeIcons[option]}
                        selected={isSelected}
                        dimmed={selected.length > 0 && !isSelected}
                        disabled={isPending || (atLimit && !isSelected)}
                        onClick={() => toggle(option)}
                      />
                    );
                  })}
                </div>

                <button
                  type="button"
                  disabled={isPending}
                  onClick={() => toggle(SplurgePriority.NONE)}
                  aria-pressed={hasNone}
                  className={`mt-5 w-full rounded-[14px] border px-6 py-6 font-sans text-lg transition-all duration-300 ${
                    hasNone
                      ? 'border-[var(--gold)] bg-[var(--gold-lt)] text-[var(--ink)]'
                      : 'border-[var(--gold-deep)] bg-[var(--surface)] text-[var(--gold)]'
                  }`}
                >
                  {t('travelDnaPage.yourSplurges.options.none')}
                </button>

                {fieldState.error ? (
                  <p className="mt-3 w-full text-left font-sans text-sm text-error" role="alert">
                    {fieldState.error.message}
                  </p>
                ) : null}
              </>
            );
          }}
        />

        <DnaPrimaryButton className="mt-6" loading={isPending} type="submit">
          {t('travelDnaPage.yourSplurges.continue')}
        </DnaPrimaryButton>
      </form>
    </DnaStepLayout>
  );
}

export default YourSplurges;
